import React, { Component } from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import queryString from 'query-string';

import { Loader, Paper, Row } from 'redpoints-front-components/index';
import { createContextFromLocalStore } from 'redpoints-front-common/authentication';
import { withQueryParams } from 'redpoints-front-common/components';
import {
  cast,
  historyHelper,
  isValidUrl,
  routeBuilder,
} from 'redpoints-front-common/helpers';

import rpfdate from 'redpoints-front-date';

import {
  allIncidentsClear,
  allIncidentsFetchData,
  allIncidentsFetchExportEntityPermission,
  allIncidentsFiltersFetchAssets,
  allIncidentsFiltersFetchDomains,
  allIncidentsFiltersFetchIncidentsStatus,
  allIncidentsFiltersFetchLinksStatus,
  allIncidentsFiltersFetchNonEnforcementReasons,
  allIncidentsFiltersFetchProtectionTypes,
  allIncidentsFiltersSetAppliedFilters,
} from '../../../actions/allIncidentsActions';
import {
  getAllIncidentsFiltersDomainsSelected,
  getAllIncidentsFiltersIsLoading,
  getAllIncidentsFiltersLevelStatusData,
  getAllIncidentsFiltersNonEnforcementReasonsIsLoading,
  getAllIncidentsTableData,
  getAllIncidentsTableIsLoading,
} from '../../../reducers/reporting/allIncidents';
import { castAllIncidentsFilters } from '../../../reducers/reporting/allIncidents/filters/values';
import AllIncidentsFilters from './AllIncidentsFilters';
import AllIncidentsTable from './AllIncidentsTable';
import { DEFAULT_CONTEXT_INFO, ROUTE_PATHS } from '../../../config/constants';
import { ALL_INCIDENTS_EXPORT_ENTITY_PERMISSION } from './constants';

import './index.less';

const buildQueryParameters = filters => {
  const resp = { ...filters };
  return pickBy(resp, value => value !== null);
};

export const REPORT_TYPE = {
  INTERNAL: 'all-incidents',
  CUSTOMER: 'all-incidents-customer',
};

const historyPush = (filters, reportType) => {
  const context = createContextFromLocalStore({
    context: DEFAULT_CONTEXT_INFO,
  });

  const queryParams = !isEmpty(filters)
    ? `?${queryString.stringify(buildQueryParameters(filters))}`
    : '';

  const pageReporting =
    reportType === REPORT_TYPE.INTERNAL
      ? ROUTE_PATHS.REPORTING_ALL_INCIDENTS
      : ROUTE_PATHS.REPORTING_ALL_INCIDENTS_CUSTOMER;

  historyHelper.push(routeBuilder(context, `${pageReporting}${queryParams}`));
};

const DATE_RANGE = {
  START: '2012-01-01',
  END: cast.dateToString(rpfdate()),
};

const specialQueryString = {
  [REPORT_TYPE.INTERNAL]: {
    critical: {
      detectionDateFrom: DATE_RANGE.START,
      detectionDateTo: DATE_RANGE.END,
      enforcementStatusGroupId: 1,
      priorityId: 1,
      incidentStatusGroupId: [1, 3, 4, 7, 8],
    },
    bookmark: {
      detectionDateFrom: DATE_RANGE.START,
      detectionDateTo: DATE_RANGE.END,
      watch: true,
    },
  },
  [REPORT_TYPE.CUSTOMER]: {
    critical: {
      detectionDateFrom: DATE_RANGE.START,
      detectionDateTo: DATE_RANGE.END,
      enforcementStatusGroupId: 1,
      priorityId: 1,
      incidentStatusGroupId: [1, 3, 4, 7],
    },
    bookmark: {
      detectionDateFrom: DATE_RANGE.START,
      detectionDateTo: DATE_RANGE.END,
      watch: true,
    },
  },
};

class AllIncidents extends Component {
  static getReportType(location) {
    const { pathname } = location;
    const pathnameSplitted = pathname.split('/');
    return pathnameSplitted[pathnameSplitted.length - 1];
  }

  componentDidMount() {
    const {
      allIncidentsFetchData,
      allIncidentsFetchExportEntityPermission,
      allIncidentsFiltersFetchAssets,
      allIncidentsFiltersFetchDomains,
      allIncidentsFiltersFetchIncidentsStatus,
      allIncidentsFiltersFetchLinksStatus,
      allIncidentsFiltersFetchProtectionTypes,
      allIncidentsFiltersSetAppliedFilters,
      allIncidentsFiltersFetchNonEnforcementReasons,
      location,
      queryParams,
    } = this.props;
    const filters = castAllIncidentsFilters(queryParams);

    allIncidentsFiltersFetchAssets({
      reportType: AllIncidents.getReportType(location),
    });
    allIncidentsFetchExportEntityPermission({
      entity: ALL_INCIDENTS_EXPORT_ENTITY_PERMISSION.BRAND,
    });
    allIncidentsFetchExportEntityPermission({
      entity: ALL_INCIDENTS_EXPORT_ENTITY_PERMISSION.PIRACY,
    });
    allIncidentsFiltersFetchProtectionTypes();
    allIncidentsFiltersFetchIncidentsStatus();
    allIncidentsFiltersFetchLinksStatus();
    allIncidentsFiltersFetchNonEnforcementReasons({
      domainId: filters.domainId,
    });

    allIncidentsFiltersSetAppliedFilters({ ...queryParams });

    if (!isEmpty(queryParams) && queryParams.domainId) {
      allIncidentsFiltersFetchDomains({ url: undefined });
    }

    if (queryParams.linkUrl && isValidUrl(queryParams.linkUrl)) {
      allIncidentsFetchData({
        filters,
      });
    }
  }

  componentDidUpdate(prevProps) {
    this.redirectAndFetchDataFromSpecialQueryParam();
    this.updateFetchNonEnforcementReasons(prevProps);
  }

  componentWillUnmount() {
    this.props.allIncidentsClear();
  }

  shouldComponentUpdate({
    domainsSelected: nextDomainsSelected,
    isLoading: nextIsLoading,
    isTableLoading: nextIsTableLoading,
    queryParams: { special: nextSpecial } = {},
  }) {
    const {
      isLoading,
      isTableLoading,
      queryParams: { special } = {},
      domainsSelected,
    } = this.props;
    return (
      domainsSelected !== nextDomainsSelected ||
      isLoading !== nextIsLoading ||
      isTableLoading !== nextIsTableLoading ||
      nextSpecial !== special
    );
  }

  updateFetchNonEnforcementReasons({ domainsSelected: prevDomainsSelected }) {
    const {
      allIncidentsFiltersFetchNonEnforcementReasons,
      domainsSelected,
      isLoadingNonEnforcementReasons,
    } = this.props;
    if (
      domainsSelected !== prevDomainsSelected &&
      !isLoadingNonEnforcementReasons
    ) {
      allIncidentsFiltersFetchNonEnforcementReasons({
        domainId: domainsSelected ?? undefined,
      });
    }
  }

  redirectAndFetchDataFromSpecialQueryParam() {
    const {
      allIncidentsFetchData,
      incidentLevelStatusData,
      isLoading,
      location,
      queryParams: { special } = {},
      allIncidentsFiltersSetAppliedFilters,
    } = this.props;

    const reportType = AllIncidents.getReportType(location);

    const queryParamsSpecial =
      reportType &&
      specialQueryString[reportType] &&
      specialQueryString[reportType][special];

    const shouldRedirectAndFetch = Boolean(
      !isLoading && queryParamsSpecial && incidentLevelStatusData
    );

    if (shouldRedirectAndFetch) {
      allIncidentsFiltersSetAppliedFilters({ ...queryParamsSpecial });
      historyPush(queryParamsSpecial, reportType);
      allIncidentsFetchData({
        filters: queryParamsSpecial,
        incidentLevelStatusData,
      });
    }
  }

  render() {
    const { incidentsData, isLoading, isTableLoading, location } = this.props;
    const reportType = AllIncidents.getReportType(location);
    const isTableFirstFetching = !incidentsData?.content && isTableLoading;

    return (
      <Paper>
        <Loader isLoading={isLoading}>
          <AllIncidentsFilters reportType={reportType} />
          <Row className="mt-20">
            <Loader isLoading={isTableFirstFetching}>
              <AllIncidentsTable reportType={reportType} />
            </Loader>
          </Row>
        </Loader>
      </Paper>
    );
  }
}

const mapStateToProps = state => ({
  incidentLevelStatusData: getAllIncidentsFiltersLevelStatusData(state),
  incidentsData: getAllIncidentsTableData(state),
  isLoading: getAllIncidentsFiltersIsLoading(state),
  isLoadingNonEnforcementReasons: getAllIncidentsFiltersNonEnforcementReasonsIsLoading(
    state
  ),
  isTableLoading: getAllIncidentsTableIsLoading(state),
  domainsSelected: getAllIncidentsFiltersDomainsSelected(state),
});

export const AllIncidentsConnected = connect(mapStateToProps, {
  allIncidentsClear,
  allIncidentsFetchData,
  allIncidentsFetchExportEntityPermission,
  allIncidentsFiltersFetchAssets,
  allIncidentsFiltersFetchDomains,
  allIncidentsFiltersFetchIncidentsStatus,
  allIncidentsFiltersFetchLinksStatus,
  allIncidentsFiltersFetchNonEnforcementReasons,
  allIncidentsFiltersFetchProtectionTypes,
  allIncidentsFiltersSetAppliedFilters,
})(AllIncidents);

export default withQueryParams(AllIncidentsConnected);
